import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { SskSigningScreen } from '@/components/ssk-signing-screen';
import { FeatureFlag } from '@/enums/feature-flag';
import { useFeatureFlag } from '@/hooks/use-feature-flag';
import { canUseSskBridge, requestBridgeSigning } from '@/utils/bridge-signing';
import { requestExtensionSigning } from '@/utils/extension-signing';
import { makeFullPath } from '@/utils/path-utils';
import {
  extractSigma1FromXml,
  replaceSignatureValueInXml,
  autoSubmitToAcs,
} from '@/utils/split-saml-xml';
import {
  SSK_FAILURE_COPY,
  SSK_SSO_TYPE,
  type SskFailureBucket,
  reportSskFailure,
  supportMailto,
  toSskFailureBucket,
} from '@/utils/ssk-failure';
import type { SplitSamlCompletionData } from '@/utils/sso-utils';
import { generateUUID } from '@/utils/tab-id';
import { combineSignatures } from '@/utils/threshold-crypto';

type SigningPhase = 'waiting' | 'signing' | 'redirecting' | 'error';
type SigningState = SplitSamlCompletionData & { username: string };

/**
 * Page that completes the split SAML threshold RSA signing flow.
 *
 * Receives partial signing data (sigma1 in SAML XML) via location.state,
 * requests the second partial signature from the browser extension,
 * combines signatures, and auto-submits the final SAML response to the SP.
 */
export default function CompleteSigningPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [phase, setPhase] = useState<SigningPhase>('waiting');
  const [failure, setFailure] = useState<{
    bucket: SskFailureBucket;
    supportMailto: string;
  } | null>(null);
  const hasStarted = useRef(false);

  const forwardPartialOnFailure = useFeatureFlag(
    FeatureFlag.SPLIT_SAML_DEMO_PARTIAL_SIGNATURE
  );
  // Read at failure time, not mount time: the gate resolves async after mount.
  const forwardPartialRef = useRef(forwardPartialOnFailure);
  useEffect(() => {
    forwardPartialRef.current = forwardPartialOnFailure;
  }, [forwardPartialOnFailure]);

  const completionData = location.state as SigningState | null;

  useEffect(() => {
    if (hasStarted.current) {
      return;
    }

    hasStarted.current = true;

    if (!completionData) {
      navigate(makeFullPath('/auth-error'), { replace: true });

      return;
    }

    performSecondSigning(completionData);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount
  }, []);

  async function performSecondSigning(data: SigningState) {
    try {
      setPhase('waiting');

      const secondSig = canUseSskBridge()
        ? await requestBridgeSigning(data.signedInfoHash)
        : await requestExtensionSigning(data.signedInfoHash);

      setPhase('signing');

      const samlXml = atob(data.samlResponseB64);
      const sigma1Bytes = extractSigma1FromXml(samlXml);

      const combinedBytes = combineSignatures(
        sigma1Bytes,
        secondSig.sigma2Bytes,
        secondSig.modulus
      );

      const combinedB64 = btoa(String.fromCharCode(...combinedBytes));

      const finalXml = replaceSignatureValueInXml(samlXml, combinedB64);
      const finalResponseB64 = btoa(finalXml);

      setPhase('redirecting');
      autoSubmitToAcs(finalResponseB64, data.acsUrl, data.relayState);
    } catch (error) {
      console.error('Split SAML second signing failed:', error);

      if (forwardPartialRef.current) {
        // Demo: forward the sigma1-only response so the SP rejects an
        // assertion that was never signed by the second share.
        setPhase('redirecting');
        autoSubmitToAcs(data.samlResponseB64, data.acsUrl, data.relayState);

        return;
      }

      const bucket = toSskFailureBucket(error);
      const referenceId = generateUUID();
      setPhase('error');
      setFailure({
        bucket,
        supportMailto: supportMailto({
          supportEmail: data.supportEmail,
          appName: data.appName,
          username: data.username,
          referenceId,
        }),
      });
      void reportSskFailure({
        ssoType: SSK_SSO_TYPE.SPLIT_SAML,
        appId: data.appId,
        bucket,
        error,
        referenceId,
      });
    }
  }

  function handleRetry() {
    if (completionData) {
      setFailure(null);
      hasStarted.current = false;
      performSecondSigning(completionData);
    }
  }

  const waitingMessage = canUseSskBridge()
    ? 'Waiting for the mobile authenticator to secure connection...'
    : 'Waiting for browser extension to secure connection...';

  return (
    <SskSigningScreen
      message={
        phase === 'redirecting'
          ? 'Signing into application using SSK...'
          : waitingMessage
      }
      redirecting={phase === 'redirecting'}
      error={
        phase === 'error' && failure
          ? {
              copy: SSK_FAILURE_COPY[failure.bucket],
              supportMailto: failure.supportMailto,
              onRetry: handleRetry,
            }
          : null
      }
    />
  );
}
